import {
  Box,
  Divider,
  Group,
  Image,
  Loader,
  Paper,
  SimpleGrid,
  Space,
  Text,
  ThemeIcon,
  Title,
  Select,
  Button,
  Skeleton,
  Overlay,
  Stack,
  Alert,
  Badge,
  Flex,
} from "@mantine/core";
import { DatePickerInput } from '@mantine/dates';
import React, { useState, useEffect } from "react";
import { StatsSecond } from "./components/StatsSecond";
import { useGetKpisQuery } from "./api/features/queriesApi";
import { useGetSoldeWaveQuery } from "../../redux/features/waveApi";
import { School, Users, Filter, Calendar, AlertCircle, X } from "tabler-icons-react";
import { useNavigate } from "react-router-dom";
import { notifications } from '@mantine/notifications';

function DashboardPage() {
  const [filterType, setFilterType] = useState("month"); // Filtre par défaut
  const [monthName, setMonthName] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Construction des paramètres de requête
  const buildQueryParams = () => {
    const params = {};
    
    if (filterType === 'week' || filterType === 'month') {
      params.filter = filterType;
    } else if (filterType === 'month_name' && monthName) {
      params.month_name = monthName;
      if (year) params.year = year;
    } else if (filterType === 'custom' && startDate && endDate) {
      params.start_date = startDate.toISOString().split('T')[0];
      params.end_date = endDate.toISOString().split('T')[0];
    } else if (initialLoad || filterType === 'month') {
      // Filtre par défaut
      params.filter = "month";
    }
    
    return params;
  };

  const queryParams = buildQueryParams();
  const { data, isLoading, isFetching, error, refetch } = useGetKpisQuery(queryParams);
  const { data:waveData} = useGetSoldeWaveQuery();
  console.log({waveData});
  
  const navigation = useNavigate();

  useEffect(() => {
    if (initialLoad && data) {
      setInitialLoad(false);
    }
  }, [data, initialLoad]);

  useEffect(() => {
    if (error) {
      notifications.show({
        title: 'Erreur de chargement',
        message: 'Une erreur est survenue lors du chargement des statistiques',
        color: 'red',
        icon: <AlertCircle size="1rem" />,
      });
    }
  }, [error]);

  const navigateToPath = (to) => {
    navigation(`/admin/${to.toLowerCase()}`);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setInitialLoad(false);
    
    // Reset autres valeurs selon le type de filtre
    if (value !== 'month_name') {
      setMonthName(null);
    }
    if (value !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const clearAllFilters = () => {
    setFilterType("month"); // Retour au filtre par défaut
    setMonthName(null);
    setStartDate(null);
    setEndDate(null);
    setYear(new Date().getFullYear());
    setInitialLoad(false);
  };

  const getFilterDisplayText = () => {
    if (data?.filter_info?.period_label) {
      return data.filter_info.period_label;
    }
    
    if (filterType === "month" || (initialLoad && !filterType)) return "Ce mois";
    if (filterType === "week") return "Cette semaine";
    if (filterType === "month_name" && monthName) {
      return `${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
    }
    if (filterType === "custom" && startDate && endDate) {
      return `Du ${startDate.toLocaleDateString('fr-FR')} au ${endDate.toLocaleDateString('fr-FR')}`;
    }
    return "Aucun filtre";
  };

  const isFilterActive = () => {
    return filterType && filterType !== 'month' || monthName || (startDate && endDate);
  };

  const canApplyCustomFilter = () => {
    return filterType === 'custom' && startDate && endDate;
  };

  const canApplyMonthFilter = () => {
    return filterType === 'month_name' && monthName;
  };

  // Options pour les mois
  const monthOptions = [
    { value: 'janvier', label: 'Janvier' },
    { value: 'février', label: 'Février' },
    { value: 'mars', label: 'Mars' },
    { value: 'avril', label: 'Avril' },
    { value: 'mai', label: 'Mai' },
    { value: 'juin', label: 'Juin' },
    { value: 'juillet', label: 'Juillet' },
    { value: 'août', label: 'Août' },
    { value: 'septembre', label: 'Septembre' },
    { value: 'octobre', label: 'Octobre' },
    { value: 'novembre', label: 'Novembre' },
    { value: 'décembre', label: 'Décembre' },
  ];

  // Options pour les années (5 dernières années + année courante)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => ({
    value: (currentYear - i).toString(),
    label: (currentYear - i).toString()
  }));

  // Validation des dates personnalisées
  const validateCustomDates = () => {
    if (startDate && endDate && startDate > endDate) {
      notifications.show({
        title: 'Dates invalides',
        message: 'La date de début doit être antérieure à la date de fin',
        color: 'red',
      });
      return false;
    }
    return true;
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') {
      setStartDate(value);
      if (endDate && value > endDate) {
        setEndDate(null);
      }
    } else {
      setEndDate(value);
    }
  };

  // Composant pour les cartes en loading
  const LoadingCards = () => (
    <SimpleGrid
      cols={3}
      breakpoints={[
        { maxWidth: "lg", cols: 3 },
        { maxWidth: "md", cols: 3 },
        { maxWidth: "sm", cols: 2 },
        { maxWidth: "xs", cols: 1 },
      ]}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Paper p="md" radius="md" shadow="sm" key={index}>
          <Group position="apart">
            <div style={{ flex: 1 }}>
              <Skeleton height={12} width="60%" mb={10} />
              <Skeleton height={24} width="40%" />
            </div>
            <Skeleton height={38} width={38} radius="md" />
          </Group>
        </Paper>
      ))}
    </SimpleGrid>
  );

  // Composant pour les contrôles de filtre
  const FilterControls = () => (
    <Stack spacing="xs">
      <Flex gap="sm" align="center" wrap="wrap">
        <Select
          placeholder="Type de filtre"
          data={[
            { value: "week", label: "Cette semaine" },
            { value: "month", label: "Ce mois" },
            { value: "month_name", label: "Mois spécifique" },
            { value: "custom", label: "Période personnalisée" },
          ]}
          value={filterType}
          onChange={handleFilterChange}
          icon={<Filter size={16} />}
          style={{ minWidth: 180 }}
          disabled={isFetching}
        />

        {filterType === 'month_name' && (
          <>
            <Select
              placeholder="Mois"
              data={monthOptions}
              value={monthName}
              onChange={setMonthName}
              style={{ minWidth: 120 }}
              disabled={isFetching}
              clearable
            />
            <Select
              placeholder="Année"
              data={yearOptions}
              value={year?.toString()}
              onChange={(value) => setYear(parseInt(value))}
              style={{ minWidth: 80 }}
              disabled={isFetching}
            />
          </>
        )}

        {filterType === 'custom' && (
          <>
            <DatePickerInput
              placeholder="Date début"
              value={startDate}
              onChange={(value) => handleDateChange('start', value)}
              style={{ minWidth: 140 }}
              disabled={isFetching}
              maxDate={new Date()}
              clearable
              locale="fr"
            />
            <DatePickerInput
              placeholder="Date fin"
              value={endDate}
              onChange={(value) => handleDateChange('end', value)}
              style={{ minWidth: 140 }}
              disabled={isFetching}
              minDate={startDate}
              maxDate={new Date()}
              clearable
              locale="fr"
            />
          </>
        )}

        {isFilterActive() && (
          <Button
            variant="subtle"
            color="gray"
            onClick={clearAllFilters}
            size="sm"
            leftIcon={<X size={14} />}
            disabled={isFetching}
          >
            Reset
          </Button>
        )}

        <Button
          onClick={() => refetch()}
          size="sm"
          variant="light"
          loading={isFetching}
          disabled={
            (filterType === 'custom' && !canApplyCustomFilter()) ||
            (filterType === 'month_name' && !canApplyMonthFilter())
          }
        >
          {isFetching ? 'Chargement...' : 'Actualiser'}
        </Button>
      </Flex>

      {/* Messages d'aide */}
      {filterType === 'custom' && (!startDate || !endDate) && (
        <Text size="xs" color="dimmed">
          Sélectionnez une date de début et une date de fin
        </Text>
      )}
      {filterType === 'month_name' && !monthName && (
        <Text size="xs" color="dimmed">
          Sélectionnez un mois
        </Text>
      )}
    </Stack>
  );

  return (
    <Box pb={30}>
      {/* En-tête avec filtres */}
      <Paper
        p="md"
        radius="md"
        mb="lg"
        shadow="sm"
        bg={isFilterActive() ? "#f8f9fa" : undefined}
        withBorder={isFilterActive()}
      >
        <Stack spacing="md">
          <Group position="apart" align="flex-start">
            <Group>
              <Title order={2}>📊 Tableau de bord</Title>
              {(isFilterActive() || data?.filter_info?.has_filter) && (
                <Badge 
                  color="blue" 
                  variant="light"
                  size="lg"
                  leftSection={<Calendar size={14} />}
                >
                  {getFilterDisplayText()}
                </Badge>
              )}
            </Group>
          </Group>
          
          <FilterControls />
        </Stack>
      </Paper>

      {/* Alerte en cas d'erreur */}
      {error && (
        <Alert
          icon={<AlertCircle size="1rem" />}
          title="Erreur de chargement"
          color="red"
          mb="lg"
          variant="light"
        >
          {error.data?.message || error.message || "Une erreur est survenue lors du chargement des données"}
          <Button 
            variant="light" 
            size="xs" 
            onClick={() => refetch()} 
            mt="xs"
          >
            Réessayer
          </Button>
        </Alert>
      )}

      {/* Zone de contenu principal */}
      <Box style={{ position: 'relative' }}>
        {/* Overlay de loading pendant le fetching */}
        {isFetching && !isLoading && (
          <Overlay opacity={0.4} color="#000" zIndex={100}>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '200px'
            }}>
              <Loader size="lg" color="blue" />
              <Text mt="md" size="sm" color="white" weight={500}>
                Mise à jour des données...
              </Text>
            </div>
          </Overlay>
        )}

        {isLoading ? (
          <LoadingCards />
        ) : (
          <>
            {data?.data?.map((item, index) =>
              item?.type === "default" ? (
                <SimpleGrid
                  cols={3}
                  breakpoints={[
                    { maxWidth: "lg", cols: 3 },
                    { maxWidth: "md", cols: 3 },
                    { maxWidth: "sm", cols: 2 },
                    { maxWidth: "xs", cols: 1 },
                  ]}
                  key={index}
                  style={{ opacity: isFetching ? 0.7 : 1 }}
                >
                  {/* Carte Wave */}
                  <Paper p="md" radius="md" bg={"#1dc4ff"} shadow="sm">
                    <Group position="apart">
                      <div>
                        <Text tt="uppercase" fw={700} fz="lg" color="#fff">
                          Wave
                        </Text>
                        <Text fw={700} fz="xl" mt={10} color="#fff" weight={800}>
                          {waveData?.solde?.amount || 'N/A'} {data?.wave_balance?.currency || 'XOF'}
                        </Text>
                      </div>
                      <Image
                        src={"https://www.wave.com/img/nav-logo.png"}
                        width={100}
                      />
                    </Group>
                  </Paper>

                  {/* Cartes default */}
                  {item?.data?.map((cardItem, cardIndex) => (
                    <Paper
                      p="md"
                      radius="md"
                      key={cardIndex}
                      shadow="sm"
                      style={{ 
                        cursor: isFetching ? "not-allowed" : "pointer",
                        pointerEvents: isFetching ? "none" : "auto",
                        transition: "all 0.2s ease"
                      }}
                      onClick={() => !isFetching && cardItem.path && navigateToPath(cardItem.path)}
                      sx={(theme) => ({
                        '&:hover': {
                          transform: isFetching ? 'none' : 'translateY(-2px)',
                          boxShadow: isFetching ? undefined : theme.shadows.md,
                        },
                      })}
                    >
                      <Group position="apart">
                        <div>
                          <Text c="dimmed" tt="uppercase" fw={700} fz="xs">
                            {cardItem.name}
                          </Text>
                          <Text fw={700} fz="xl" mt={10}>
                            {cardItem.value?.toLocaleString() || cardItem.value}
                          </Text>
                          {cardItem.description && (
                            <Text size="xs" color="dimmed" mt={5}>
                              {cardItem.description}
                            </Text>
                          )}
                        </div>
                        <ThemeIcon
                          color="gray"
                          variant="light"
                          size={38}
                          radius="md"
                        >
                          {cardItem.name?.toUpperCase() === "COACHS" ? (
                            <Users size="1.8rem" />
                          ) : (
                            <School size="1.8rem" />
                          )}
                        </ThemeIcon>
                      </Group>
                    </Paper>
                  ))}
                </SimpleGrid>
              ) : (
                <div key={index} style={{ opacity: isFetching ? 0.7 : 1 }}>
                  <Space h={30} />
                  <Divider
                    label={
                      <Group>
                        <Title order={4}>{item?.type}</Title>
                        {data?.filter_info?.period_label && item?.type?.includes("filtré") && (
                          <Badge size="sm" color="blue" variant="outline">
                            {data.filter_info.period_label}
                          </Badge>
                        )}
                      </Group>
                    }
                    mb={10}
                  />

                  <StatsSecond
                    data={item?.data?.map(
                      ({ name, value, description, path, filter_period }) => ({
                        title: name,
                        value,
                        description: filter_period ? `${description}` : description,
                        path,
                      })
                    )}
                    loading={isFetching}
                  />
                </div>
              )
            )}
          </>
        )}

        {/* Message si aucune donnée */}
        {!isLoading && !data?.data?.length && (
          <Paper p="xl" radius="md" ta="center">
            <Text color="dimmed" size="lg">
              Aucune donnée disponible
            </Text>
            <Button 
              variant="light" 
              onClick={() => refetch()} 
              mt="md"
            >
              Actualiser
            </Button>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default DashboardPage;